import React, { useState, Children } from 'react';
import { useFormContext } from 'react-hook-form';
import { CheckCircleIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, AlertCircleIcon } from 'lucide-react';
interface FormSummaryProps {
  onEditStep: (stepIndex: number) => void;
}
const FormSummary: React.FC<FormSummaryProps> = ({
  onEditStep
}) => {
  const {
    watch,
    setValue,
    register
  } = useFormContext();
  const [expandedSections, setExpandedSections] = useState<string[]>(['clientInfo', 'spouseInfo', 'existingDocuments', 'beneficiaries', 'specialNeeds', 'assets', 'financialPOA', 'healthcarePOA']);
  const formData = watch();
  const isConfirmed = formData.termsConfirm === 'true';
  const handleConfirmChange = (checked: boolean) => {
    setValue('termsConfirm', checked ? 'true' : 'false');
  };
  const toggleSection = (section: string) => {
    if (expandedSections.includes(section)) {
      setExpandedSections(expandedSections.filter(s => s !== section));
    } else {
      setExpandedSections([...expandedSections, section]);
    }
  };
  const isSectionExpanded = (section: string) => expandedSections.includes(section);
  const SummarySection = ({
    title,
    section,
    stepIndex,
    children
  }: {
    title: string;
    section: string;
    stepIndex: number;
    children: React.ReactNode;
  }) => <div className="border border-slate-200 rounded-md mb-4 overflow-hidden" data-id="element-52" data-mp="3pymcesznxi05">
      <div className="flex justify-between items-center w-full px-4 py-3 bg-slate-50" data-id="element-53" data-mp="hjvwxewdbpcq">
        <button type="button" className="flex items-center flex-1 text-left" onClick={() => toggleSection(section)} data-id="element-54" data-mp="lj4oqe7uld5n">
          <h3 className="font-medium text-navy-800 mr-2" data-id="element-55" data-mp="cjhmjq7ao3bj">{title}</h3>
          {isSectionExpanded(section) ? <ChevronUpIcon size={20} className="text-navy-600" data-id="element-56" data-mp="2savotkqbj0ib" /> : <ChevronDownIcon size={20} className="text-navy-600" data-id="element-57" data-mp="2scypqzt0ee1t" />}
        </button>
        <button type="button" onClick={() => onEditStep(stepIndex)} className="flex items-center text-sm text-navy-600 hover:text-navy-800 ml-4" data-id="element-58" data-mp="2x7ce4i997kwz">
          <EditIcon size={16} className="mr-1" data-id="element-59" data-mp="296si2zhqjvzp" />
          Edit
        </button>
      </div>
      {isSectionExpanded(section) && <div className="p-4 bg-white" data-id="element-60" data-mp="1zgfqinbi72k2">{children}</div>}
    </div>;
  const SummaryItem = ({
    label,
    value
  }: {
    label: string;
    value: any;
  }) => {
    if (!value || value === '') return null;
    return <div className="py-2 border-b border-slate-100 last:border-0" data-id="element-61" data-mp="1phi6nxp0vx7e">
        <div className="text-sm text-slate-500" data-id="element-62" data-mp="1pkcqpo44jg0a">{label}</div>
        <div className="font-medium mt-1" data-id="element-63" data-mp="1lh7qjs7inqet">{value}</div>
      </div>;
  };
  const EmptyState = () => <div className="text-center py-4 text-slate-500 italic" data-id="element-64" data-mp="1gimw69ey40ya">
      No information provided
    </div>;
  return <div data-id="element-65" data-mp="3fto1h6b9bsq0">
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6 flex items-start" data-id="element-66" data-mp="3fscxb5dwgiyd">
        <AlertCircleIcon className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" size={20} data-id="element-67" data-mp="3at9snm1u0eqx" />
        <div data-id="element-68" data-mp="3ndyptf3wgy22">
          <h3 className="font-medium text-blue-800" data-id="element-69" data-mp="3ndov5e7wu2q1">Review Your Information</h3>
          <p className="text-blue-700 mt-1" data-id="element-70" data-mp="3s4ou0p0lauv6">
            Please carefully review all the information you have provided below.
            You can edit any section by clicking the "Edit" button next to each
            section heading. Once you have confirmed everything is correct,
            check the confirmation box at the bottom and submit your form.
          </p>
        </div>
      </div>

      {/* Client Information */}
      <SummarySection title="1. Client Information" section="clientInfo" stepIndex={0} data-id="element-71" data-mp="39dxoxznmxqml">
        {formData.clientInfo?.fullName ? <div className="space-y-1" data-id="element-72" data-mp="3ddfzc1htyjb6">
            <SummaryItem label="Existing Client" value={formData.clientInfo?.existingClient === 'true' ? 'Yes' : 'No'} data-id="element-73" data-mp="ooq38aeas2xl" />
            <SummaryItem label="How did you hear about us?" value={formData.clientInfo?.referralSource} data-id="element-74" data-mp="r7l3sjddonnk" />
            <SummaryItem label="Full Name" value={formData.clientInfo?.fullName} data-id="element-75" data-mp="mb4f9o29o79y" />
            <SummaryItem label="Age" value={formData.clientInfo?.age} data-id="element-76" data-mp="w7h8ylxtal4p" />
            <SummaryItem label="Marital Status" value={formData.clientInfo?.maritalStatus} data-id="element-77" data-mp="w666qqk0llfp" />
            <SummaryItem label="Address" value={formData.clientInfo?.address} data-id="element-78" data-mp="1585qhqmez3mc" />
            <SummaryItem label="Phone Number" value={formData.clientInfo?.phone} data-id="element-79" data-mp="1562wywm1yscv" />
            <SummaryItem label="Email" value={formData.clientInfo?.email} data-id="element-80" data-mp="1ey8qiarydhu6" />
            <SummaryItem label="Occupation" value={formData.clientInfo?.occupation} data-id="element-81" data-mp="1f0bdsn1q0wv7" />
            <SummaryItem label="Employer Name" value={formData.clientInfo?.employerName} data-id="element-82" data-mp="1ower8p20whxg" />
            <SummaryItem label="Business Address" value={formData.clientInfo?.businessAddress} data-id="element-83" data-mp="27li4iwl374o9" />
            <SummaryItem label="Business Phone" value={formData.clientInfo?.businessPhone} data-id="element-84" data-mp="2chfl9409id1k" />
            <SummaryItem label="Citizenship" value={formData.clientInfo?.citizenship} data-id="element-85" data-mp="23ic3q4gqu5p6" />
          </div> : <EmptyState data-id="element-86" data-mp="1osmkmu8p3h0m" />}
      </SummarySection>

      {/* Spouse & Family */}
      <SummarySection title="2. Spouse & Family" section="spouseInfo" stepIndex={1} data-id="element-87" data-mp="1tm8qa2gfykmx">
        {formData.spouseInfo?.name || formData.previousMarriages || formData.children || formData.grandchildren ? <div className="space-y-4" data-id="element-88" data-mp="15ujv8ec8ji2a">
            {formData.spouseInfo?.name && <div data-id="element-89" data-mp="1atwrtqsws3i2">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-90" data-mp="1aufz8dkxn9hu">
                  Spouse Information
                </h4>
                <div className="space-y-1" data-id="element-91" data-mp="3ck5byjp3kxvl">
                  <SummaryItem label="Spouse's Name" value={formData.spouseInfo?.name} data-id="element-92" data-mp="37snaluj5wva3" />
                  <SummaryItem label="Spouse's Age" value={formData.spouseInfo?.age} data-id="element-93" data-mp="33rtnh3djnki9" />
                </div>
              </div>}
            {formData.previousMarriages && <div data-id="element-94" data-mp="3vfvfibcoi2bl">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-95" data-mp="3rgw4qa49nbc8">
                  Previous Marriages
                </h4>
                <div className="space-y-1" data-id="element-96" data-mp="3mggrdijzi3u9">
                  <SummaryItem label="Previously Married" value={formData.previousMarriages?.exists === 'true' ? 'Yes' : 'No'} data-id="element-97" data-mp="3mjmdcoxbgplr" />
                  {formData.previousMarriages?.exists === 'true' && <SummaryItem label="Ex-Spouse Name" value={formData.previousMarriages?.exSpouseName} data-id="element-98" data-mp="1w0e7tqe2sfcc" />}
                </div>
              </div>}
            {formData.domesticContracts && <div data-id="element-99" data-mp="2aohfd47rnz39">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-100" data-mp="2ap0z8qgz8xjx">
                  Domestic Contracts
                </h4>
                <div className="space-y-1" data-id="element-101" data-mp="2epkwrv5gq36l">
                  <SummaryItem label="Has Domestic Contracts" value={formData.domesticContracts?.exists === 'true' ? 'Yes' : 'No'} data-id="element-102" data-mp="2joe8pj53pm4n" />
                </div>
              </div>}
            {formData.supportObligations && <div data-id="element-103" data-mp="z92l486xzlbu">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-104" data-mp="z8kgooa4m0su">
                  Support Obligations
                </h4>
                <div className="space-y-1" data-id="element-105" data-mp="315bee6qv30dz">
                  <SummaryItem label="Has Support Obligations" value={formData.supportObligations?.exists === 'true' ? 'Yes' : 'No'} data-id="element-106" data-mp="3149tzbnyh1jl" />
                  {formData.supportObligations?.exists === 'true' && <SummaryItem label="Details" value={formData.supportObligations?.details} data-id="element-107" data-mp="2i8jq1iwgpv32" />}
                </div>
              </div>}
            {formData.children && formData.children.length > 0 && <div data-id="element-108" data-mp="2n5a27v55c6j7">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-109" data-mp="2n8xwiwif0wyz">Children</h4>
                {formData.children.map((child: any, index: number) => <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200" data-id="element-110" data-mp="2hdv70qslb03" data-mpi={index}>
                    <SummaryItem label="Name" value={child.name} data-id="element-111" data-mp="a281urczz6qp" />
                    <SummaryItem label="Age" value={child.age} data-id="element-112" data-mp="f09mhgq2ne4a" />
                  </div>)}
              </div>}
            {formData.grandchildren && formData.grandchildren.length > 0 && <div data-id="element-113" data-mp="51k9a194ehud">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-114" data-mp="52vfs8tya3p6">
                  Grandchildren
                </h4>
                {formData.grandchildren.map((grandchild: any, index: number) => <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200" data-id="element-115" data-mp="j136hz0e88rw" data-mpi={index}>
                      <SummaryItem label="Name" value={grandchild.name} data-id="element-116" data-mp="nybonzljkct5" />
                      <SummaryItem label="Age" value={grandchild.age} data-id="element-117" data-mp="1bofumdm7conb" />
                      <SummaryItem label="Parent" value={grandchild.parent} data-id="element-118" data-mp="1gfxm8b2c5cvm" />
                    </div>)}
              </div>}
            {formData.deceasedChildren && <div data-id="element-119" data-mp="12lm4c4dinkm2">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-120" data-mp="12k1a6fv463oq">
                  Deceased Children
                </h4>
                <div className="space-y-1" data-id="element-121" data-mp="olb7mkpizbsz">
                  <SummaryItem label="Has Deceased Children" value={formData.deceasedChildren?.exists === 'true' ? 'Yes' : 'No'} data-id="element-122" data-mp="tk4o8m03zixd" />
                  {formData.deceasedChildren?.exists === 'true' && <SummaryItem label="Names" value={formData.deceasedChildren?.names} data-id="element-123" data-mp="5wwjq6ai8dwe" />}
                </div>
              </div>}
          </div> : <EmptyState data-id="element-124" data-mp="w8iyswh4ioz" />}
      </SummarySection>

      {/* Existing Estate Documents */}
      <SummarySection title="3. Existing Estate Documents" section="existingDocuments" stepIndex={2} data-id="element-125" data-mp="3det4e4bcztlc">
        {formData.existingDocuments || formData.estateTrustee || formData.alternateTrustee ? <div className="space-y-4" data-id="element-126" data-mp="3nf225ml01n2u">
            {formData.existingDocuments && <div data-id="element-127" data-mp="3id32amogx8an">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-128" data-mp="3igqwlo1qlyqf">
                  Existing Will
                </h4>
                <div className="space-y-1" data-id="element-129" data-mp="3sctuuitgxd83">
                  <SummaryItem label="Has Existing Will" value={formData.existingDocuments?.hasWill === 'true' ? 'Yes' : 'No'} data-id="element-130" data-mp="3sc2bc6d5b5y6" />
                </div>
              </div>}
            {formData.estateTrustee && <div data-id="element-131" data-mp="eyh24vaci4bv">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-132" data-mp="ez0e7vdbdyhz">
                  Estate Trustee
                </h4>
                <div className="space-y-1" data-id="element-133" data-mp="m2q9hmf9ocxi">
                  <SummaryItem label="Name" value={formData.estateTrustee?.name} data-id="element-134" data-mp="qzyv5wzmiz3i" />
                  <SummaryItem label="Relationship" value={formData.estateTrustee?.relationship} data-id="element-135" data-mp="vyjnzbcjqghx" />
                  <SummaryItem label="City" value={formData.estateTrustee?.city} data-id="element-136" data-mp="15pejl6bomsg1" />
                </div>
              </div>}
            {formData.alternateTrustee && <div data-id="element-137" data-mp="1er49u8ttvlc8">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-138" data-mp="1eqm3ukhd5sr4">
                  Alternate Trustee
                </h4>
                <div className="space-y-1" data-id="element-139" data-mp="1jmjkkrwjh14f">
                  <SummaryItem label="Name" value={formData.alternateTrustee?.name} data-id="element-140" data-mp="3gi7mf3na39zf" />
                  <SummaryItem label="Relationship" value={formData.alternateTrustee?.relationship} data-id="element-141" data-mp="3li3sd8fjdbhm" />
                  <SummaryItem label="City" value={formData.alternateTrustee?.city} data-id="element-142" data-mp="3959gpayelwnk" />
                </div>
              </div>}
          </div> : <EmptyState data-id="element-143" data-mp="2z9oilnwghgj3" />}
      </SummarySection>

      {/* Beneficiaries */}
      <SummarySection title="4. Beneficiaries" section="beneficiaries" stepIndex={3} data-id="element-144" data-mp="pps4p8oh2eu">
        {formData.beneficiaries || formData.specificGifts || formData.guardians ? <div className="space-y-4" data-id="element-145" data-mp="3mzivu6kot73v">
            {formData.specificGifts && formData.specificGifts.length > 0 && <div data-id="element-146" data-mp="2bi9gnw8ld18d">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-147" data-mp="2biso2j0m8785">
                  Specific Gifts or Bequests
                </h4>
                {formData.specificGifts.map((gift: any, index: number) => <div key={index} className="mb-2 pl-4 border-l-2 border-slate-200" data-id="element-148" data-mp="3vd3rm032u11y" data-mpi={index}>
                    <SummaryItem label="Item/Amount" value={gift.item} data-id="element-149" data-mp="40so1h4udduz" />
                    <SummaryItem label="Recipient" value={gift.recipient} data-id="element-150" data-mp="3lgilfbfbmjwj" />
                  </div>)}
              </div>}
            {formData.beneficiaries && <div data-id="element-151" data-mp="hzij6r83p3gp">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-152" data-mp="hz8tz5uvr0c2">
                  Estate Distribution
                </h4>
                <div className="space-y-1" data-id="element-153" data-mp="mv7nt8yz9ajv">
                  <SummaryItem label="Distribution Type" value={formData.beneficiaries?.distribution} data-id="element-154" data-mp="7z9lf8ygn9z7" />
                  {formData.beneficiaries?.distribution === 'custom' && <SummaryItem label="Custom Plan" value={formData.beneficiaries?.customPlan} data-id="element-155" data-mp="2ssmtsl4lzi6m" />}
                  <SummaryItem label="Alternate Beneficiaries" value={formData.beneficiaries?.alternates} data-id="element-156" data-mp="1fqf7yphl7rv7" />
                  <SummaryItem label="Age of Inheritance" value={formData.beneficiaries?.ageOfInheritance} data-id="element-157" data-mp="1ot6k6riaernw" />
                  {formData.beneficiaries?.ageOfInheritance === 'custom' && <SummaryItem label="Custom Age" value={formData.beneficiaries?.customAge} data-id="element-158" data-mp="x1qqyem866op" />}
                </div>
              </div>}
            {formData.guardians && <div data-id="element-159" data-mp="11215wgrzw7fp">
                <h4 className="font-medium text-navy-700 mb-2" data-id="element-160" data-mp="111suktbita6m">
                  Guardians for Minor Children
                </h4>
                <div className="space-y-1" data-id="element-161" data-mp="9bwe3x1woi4g">
                  <SummaryItem label="Guardian" value={formData.guardians?.name} data-id="element-162" data-mp="9b4vdmteqevl" />
                  <SummaryItem label="Alternate Guardian" value={formData.guardians?.alternate} data-id="element-163" data-mp="28ettpohwlpav" />
                  <SummaryItem label="Monthly Allowance" value={formData.guardians?.monthlyAllowance ? `$${formData.guardians.monthlyAllowance}` : undefined} data-id="element-164" data-mp="2i7iv1qjqpmsh" />
                </div>
              </div>}
          </div> : <EmptyState data-id="element-165" data-mp="1zeyd345kx23g" />}
      </SummarySection>

      {/* Special Needs */}
      <SummarySection title="5. Corporate, Dependents & Special Needs" section="specialNeeds" stepIndex={4} data-id="element-166" data-mp="24fm4vs1mtmyq">
        <EmptyState data-id="element-167" data-mp="3aun4q8ts2pal" />
      </SummarySection>

      {/* Assets & Insurance */}
      <SummarySection title="6. Assets & Insurance" section="assets" stepIndex={5} data-id="element-168" data-mp="3ft6llutilaw3">
        <EmptyState data-id="element-169" data-mp="146cysn8s3hhw" />
      </SummarySection>

      {/* Financial POA */}
      <SummarySection title="7. Power of Attorney: Financial" section="financialPOA" stepIndex={6} data-id="element-170" data-mp="z7jlauthrqhq">
        <EmptyState data-id="element-171" data-mp="uf8jtnj3rwn2" />
      </SummarySection>

      {/* Healthcare POA */}
      <SummarySection title="8. Power of Attorney: Healthcare" section="healthcarePOA" stepIndex={7} data-id="element-172" data-mp="pgyptg947231">
        <EmptyState data-id="element-173" data-mp="kgspzcsoy99v" />
      </SummarySection>

      {/* Confirmation Section */}
      <div className="mt-8 bg-navy-50 p-6 rounded-lg border border-navy-200" data-id="element-174" data-mp="gfqqcuuv3v7d">
        <h3 className="text-lg font-medium text-navy-900 mb-4" data-id="element-175" data-mp="bja280nnxgua">
          Ready to Submit?
        </h3>
        <p className="text-navy-700 mb-6" data-id="element-176" data-mp="6k5qp3d9n70s">
          By submitting this form, you confirm that all information provided is
          accurate to the best of your knowledge. Your lawyer will review this
          information and may contact you for clarification if needed.
        </p>
        <div className="flex items-start" data-id="element-177" data-mp="19rjb77faav6d">
          <input type="checkbox" id="terms-confirm" checked={isConfirmed} onChange={e => handleConfirmChange(e.target.checked)} className="h-5 w-5 text-navy-600 rounded focus:ring-navy-500 mt-0.5" data-id="element-178" data-mp="19skufz6genz6" />
          <label htmlFor="terms-confirm" className="ml-3 text-navy-700" data-id="element-179" data-mp="14zrtlme3vzsd">
            <span className="font-medium" data-id="element-180" data-mp="r0s3sbeuxeum">
              I confirm that I have reviewed all the information above and it is
              accurate and complete.
            </span>
            <span className="block text-sm text-navy-600 mt-1" data-id="element-181" data-mp="h35d6ro73yut">
              You must check this box before you can submit the form.
            </span>
          </label>
        </div>
        {!isConfirmed && <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-start" data-id="element-182" data-mp="2bgn1igwfczqw">
            <AlertCircleIcon size={16} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-183" data-mp="2ggri06xc1uh4" />
            <p className="text-sm text-yellow-800" data-id="element-184" data-mp="4tfn95gknyf6">
              Please review all sections and check the confirmation box above to
              enable the submit button.
            </p>
          </div>}
      </div>

      {/* Hidden registered input for termsConfirm */}
      <input type="hidden" {...register('termsConfirm')} data-id="element-185" data-mp="9qxyj2i2apqc" />
    </div>;
};
export default FormSummary;